/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Pressable, View, useWindowDimensions } from 'react-native';
import { useTranslation } from 'react-i18next';

import BottomSheet from '@airasia-phoenix/react-native/components/BottomSheetView';
import StickyBottomBarView from '@airasia-phoenix/react-native/components/StickyBottomBarView';
import PassengerClassSelection from './PassengerClassSelectionView';
import { getColor, pd } from '@airasia-phoenix/react-native/utils/utils';
import { StyleSheet } from 'react-native-unistyles';

import Constants from './constants';
import type {
  PassengerClassBottomSheetViewProps,
  SelectedValues,
} from './types';
import { CabinClassType } from './types';
import SnackbarView, {
  SnackbarDuration,
} from '@airasia-phoenix/react-native/components/SnackbarView';
import { usePortalUsageEnabled } from '../../hooks';
import PortalWrapperView from '../PortalWrapperView/PortalWrapperView';

type AnchorLayout = { x: number; y: number; width: number; height: number };

// Portal positioning
const DROPDOWN_WIDTH = 380;
const EDGE_PADDING = 8;

// resize “settle” tuning
const RESIZE_SETTLE_MS = 120;
const STABLE_FRAMES_MAX = 5;
const STABLE_EPS = 0.5;

// stable DOM lookup id (RN Web friendly)
const PORTAL_CARD_ID = 'paxClassPortalCard';

const stop = (e?: any) => {
  e?.stopPropagation?.();
  e?.preventDefault?.();
};

const PassengerClassBottomSheetView = memo(
  (props: PassengerClassBottomSheetViewProps) => {
    const {
      isModalVisible,
      onRequestCloseModal,

      isSheetOpen,
      onSheetDidClose,
      bottomSheetViewRef,

      adultCount,
      childCount,
      infantCount,
      cabinClass,

      updateAdultCount,
      updateChildCount,
      updateInfantCount,
      updateCabinClass,

      onDone,
      anchorRef,
      hideCabinClass,
    } = props;

    const { enabled: isPortalUsageEnabled } = usePortalUsageEnabled();

    const GAP = isPortalUsageEnabled ? 3 : 54;

    const { t } = useTranslation('flights');
    const { width: windowWidth, height: windowHeight } = useWindowDimensions();

    // draft state (ONLY local while picker is open)
    const [draft, setDraft] = useState<SelectedValues>({
      adult: adultCount,
      child: childCount,
      infant: infantCount,
      cabinClass,
    });

    // Anchor measurement state (web portal positioning)
    const [anchorLayout, setAnchorLayout] = useState<AnchorLayout | null>(null);

    const [isSnackBarVisible, setIsSnackBarVisible] = useState(false);

    // reset draft when picker OPENS
    useEffect(() => {
      // Modal open (web dropdown)
      if (isModalVisible) {
        setDraft({
          adult: adultCount,
          child: childCount,
          infant: infantCount,
          cabinClass,
        });
        return;
      }

      // BottomSheet open (mobile)
      if (isSheetOpen) {
        setDraft({
          adult: adultCount,
          child: childCount,
          infant: infantCount,
          cabinClass,
        });
      }
    }, [
      isModalVisible,
      isSheetOpen,
      adultCount,
      childCount,
      infantCount,
      cabinClass,
    ]);

    const handleDraftChange = useCallback(
      (v: SelectedValues) => {
        setDraft(v);
        // NOTE: If you only want commit on "Done", remove this block.
        if (isModalVisible) {
          updateAdultCount(v.adult);
          updateChildCount(v.child);
          updateInfantCount(v.infant);
          updateCabinClass(v.cabinClass);
        }
      },
      [
        isModalVisible,
        updateAdultCount,
        updateChildCount,
        updateInfantCount,
        updateCabinClass,
      ],
    );

    // cancel: discard + close
    const cancelAndClose = useCallback(
      (e?: any) => {
        stop(e);

        setDraft({
          adult: adultCount,
          child: childCount,
          infant: infantCount,
          cabinClass,
        });

        if (isModalVisible) {
          onRequestCloseModal(e);
        } else {
          bottomSheetViewRef.current?.toggleBottomSheetView();
        }
      },
      [
        adultCount,
        childCount,
        infantCount,
        cabinClass,
        isModalVisible,
        onRequestCloseModal,
        bottomSheetViewRef,
      ],
    );

    // done: commit + close
    const donePress = useCallback(
      (e?: any) => {
        stop(e);

        updateAdultCount(draft.adult);
        updateChildCount(draft.child);
        updateInfantCount(draft.infant);
        updateCabinClass(draft.cabinClass);

        onDone(draft, e);

        if (isModalVisible) {
          onRequestCloseModal(e);
        } else {
          bottomSheetViewRef.current?.toggleBottomSheetView();
        }
      },
      [
        draft,
        updateAdultCount,
        updateChildCount,
        updateInfantCount,
        updateCabinClass,
        onDone,
        isModalVisible,
        onRequestCloseModal,
        bottomSheetViewRef,
      ],
    );

    const passengerSelectionComponent = useMemo(
      () => (
        <PassengerClassSelection
          adultCount={draft.adult}
          childCount={draft.child}
          infantCount={draft.infant}
          maxTotal={Constants.MAX_TOTAL_PASSENGER_COUNT}
          cabinClass={draft.cabinClass}
          hideCabinClass={hideCabinClass}
          adultPassengerItemProps={{
            primaryText: t('flightsSearchPage.main.adult'),
            secondaryText: t('flightsSearchPage.main.adultRange'),
          }}
          childPassengerItemProps={{
            primaryText: t('flightsSearchPage.main.child'),
            secondaryText: t('flightsSearchPage.main.childRange'),
          }}
          infantPassengerItemProps={{
            primaryText: t('flightsSearchPage.main.infant'),
            secondaryText: t('flightsSearchPage.main.infantRange'),
          }}
          cabinClassText={t('flightsSearchPage.main.cabinClassLabel')}
          onChange={handleDraftChange}
          chipGroupViewProps={{
            orientation: Constants.CLASS_CHIP_GROUP_ORIENTATION,
            chipViewProps: [
              {
                text: t('flightsSearchPage.cabinClass.economy'),
                isSelected: draft.cabinClass === CabinClassType.ECONOMY,
                disabled: false,
                width: Constants.CHIP_WIDTH,
                height: Constants.CHIP_HEIGHT,
                value: CabinClassType.ECONOMY,
              },
              {
                text: t('flightsSearchPage.cabinClass.business'),
                isSelected: draft.cabinClass === CabinClassType.BUSINESS,
                disabled: false,
                width: Constants.CHIP_WIDTH,
                height: Constants.CHIP_HEIGHT,
                value: CabinClassType.BUSINESS,
              },
              {
                text: t('flightsSearchPage.cabinClass.premium_economy'),
                isSelected: draft.cabinClass === CabinClassType.PREMIUM_ECONOMY,
                disabled: false,
                width: Constants.CHIP_WIDTH,
                height: Constants.CHIP_HEIGHT,
                value: CabinClassType.PREMIUM_ECONOMY,
              },
              {
                text: t('flightsSearchPage.cabinClass.first_class'),
                isSelected: draft.cabinClass === CabinClassType.FIRST_CLASS,
                disabled: false,
                width: Constants.CHIP_WIDTH,
                height: Constants.CHIP_HEIGHT,
                value: CabinClassType.FIRST_CLASS,
              },
            ],
          }}
        />
      ),
      [draft, handleDraftChange, t, hideCabinClass],
    );

    /**
     * =========================
     * WEB: Portal + anchored positioning + outside-click close (scroll stays enabled)
     * =========================
     */

    // Throttled measure (1 per animation frame)
    const rafId = useRef<number | null>(null);
    const measureAnchorThrottled = useCallback(() => {
      if (!anchorRef?.current) return;
      if (rafId.current != null) return;

      rafId.current = requestAnimationFrame(() => {
        rafId.current = null;
        anchorRef.current?.measureInWindow((x, y, width, height) => {
          setAnchorLayout({ x, y, width, height });
        });
      });
    }, [anchorRef]);

    useEffect(() => {
      return () => {
        if (rafId.current != null) cancelAnimationFrame(rafId.current);
      };
    }, []);

    // Measure until stable
    const measureAnchorStable = useCallback(() => {
      if (!anchorRef?.current) return;

      let frames = 0;
      let last: AnchorLayout | null = null;

      const tick = () => {
        anchorRef.current?.measureInWindow((x, y, width, height) => {
          const next: AnchorLayout = { x, y, width, height };

          const stable =
            last &&
            Math.abs(last.x - next.x) < STABLE_EPS &&
            Math.abs(last.y - next.y) < STABLE_EPS &&
            Math.abs(last.width - next.width) < STABLE_EPS &&
            Math.abs(last.height - next.height) < STABLE_EPS;

          setAnchorLayout(next);
          last = next;
          frames += 1;

          if (!stable && frames < STABLE_FRAMES_MAX) {
            requestAnimationFrame(tick);
          }
        });
      };

      requestAnimationFrame(tick);
    }, [anchorRef]);

    useEffect(() => {
      if (!isModalVisible) {
        // Optional: Clear layout when closed so it doesn't
        // show at the "old" position next time it opens
        setAnchorLayout(null);
        return;
      }
      measureAnchorStable();
    }, [isModalVisible, measureAnchorStable]);

    // On resize while open -> throttled + settle pass
    const resizeDebounce = useRef<number | null>(null);
    useEffect(() => {
      if (!isModalVisible) return;

      measureAnchorThrottled();

      if (resizeDebounce.current) window.clearTimeout(resizeDebounce.current);
      resizeDebounce.current = window.setTimeout(() => {
        measureAnchorStable();
      }, RESIZE_SETTLE_MS);

      return () => {
        if (resizeDebounce.current) window.clearTimeout(resizeDebounce.current);
      };
    }, [
      isModalVisible,
      windowWidth,
      windowHeight,
      measureAnchorThrottled,
      measureAnchorStable,
    ]);

    // On scroll while open -> throttled
    useEffect(() => {
      if (!isModalVisible) return;

      const onScroll = () => measureAnchorThrottled();
      window.addEventListener('scroll', onScroll, true);
      return () => window.removeEventListener('scroll', onScroll, true);
    }, [isModalVisible, measureAnchorThrottled]);

    // Outside click close (no overlay capturing events => scroll stays enabled)
    useEffect(() => {
      if (!isModalVisible) return;

      const getCardNode = () =>
        document.getElementById(PORTAL_CARD_ID) as HTMLElement | null;

      const onPointerDown = (e: PointerEvent) => {
        const node = getCardNode();
        const target = e.target as Node | null;

        if (node && target && !node.contains(target)) {
          onRequestCloseModal();
        }
      };

      document.addEventListener('pointerdown', onPointerDown, true);
      return () =>
        document.removeEventListener('pointerdown', onPointerDown, true);
    }, [isModalVisible, onRequestCloseModal]);

    useEffect(() => {
      const totalPaxCount =
        (draft?.adult ?? 0) + (draft?.child ?? 0) + (draft?.infant ?? 0);
      if (totalPaxCount === Constants.MAX_TOTAL_PASSENGER_COUNT) {
        setIsSnackBarVisible(true);
      }
      if (isSnackBarVisible && totalPaxCount < 9) setIsSnackBarVisible(false);
    }, [draft.adult, draft.infant, draft.child]);

    const renderSnackBar = () => {
      return (
        <View style={{ marginHorizontal: 9, bottom: isModalVisible ? 3 : 7 }}>
          <SnackbarView
            message={t('flightsSearchPage.cabinClass.maxPaxError', {
              count: Constants.MAX_TOTAL_PASSENGER_COUNT,
            })}
            duration={SnackbarDuration.SHORT}
            containerBackgroundColor={'black'}
            messageTextColor={'white'}
            onDismiss={() => setIsSnackBarVisible(false)}
          />
        </View>
      );
    };

    // Compute portal position: below + right aligned to field, clamped to screen
    const portalCardStyle = useMemo(() => {
      if (!anchorLayout)
        return isPortalUsageEnabled
          ? {}
          : { position: 'absolute' as const, top: GAP };
      const desiredLeft = anchorLayout.x + anchorLayout.width - DROPDOWN_WIDTH;

      const left = isPortalUsageEnabled
        ? Math.max(
            EDGE_PADDING,
            Math.min(desiredLeft, windowWidth - DROPDOWN_WIDTH - EDGE_PADDING),
          )
        : anchorLayout.width - DROPDOWN_WIDTH;

      const top = isPortalUsageEnabled
        ? anchorLayout.y + anchorLayout.height + GAP
        : GAP;

      return {
        position: 'absolute' as const,
        top,
        left,
        width: DROPDOWN_WIDTH,
        maxWidth: windowWidth - EDGE_PADDING * 2,
        borderRadius: 18,
        overflow: 'hidden' as const,
        backgroundColor: getColor(pd.sys.colour.container_none),
        borderWidth: 1,
        borderColor: getColor(pd.sys.colour.secondary),
        zIndex: 12,
      };
    }, [anchorLayout, isPortalUsageEnabled, windowWidth]);

    // WEB DESKTOP (Portal)
    if (isModalVisible) {
      // guard to prevent flickering at (0,0)
      if (!anchorLayout) return null;
      return (
        /* Visual overlay only (does NOT capture clicks/scroll) */
        <PortalWrapperView>
          <View
            pointerEvents="none"
            style={[
              styles.visualOverlay,
              // { backgroundColor: getColor(pd.sys.colour.overlay) },
            ]}
          />

          {/* Card only; outside click handled by document listener */}
          <Pressable
            nativeID={PORTAL_CARD_ID}
            // style={portalCardStyle ?? styles.portalFallbackCard}
            style={portalCardStyle}
            onPress={(e) => stop(e)}
          >
            <View
              style={{
                marginTop: pd.ref.space.spacing50.px,
                marginBottom: pd.ref.space.spacing20.px,
              }}
            >
              {passengerSelectionComponent}
              {isSnackBarVisible ? renderSnackBar() : null}
            </View>
          </Pressable>
        </PortalWrapperView>
      );
    }

    // NATIVE / MOBILE
    return (
      <BottomSheet
        ref={bottomSheetViewRef}
        initialHeight={Constants.PASSENGER_CLASS_BOTTOM_SHEET_HEIGHT}
        finalHeight={Constants.PASSENGER_CLASS_BOTTOM_SHEET_HEIGHT}
        showHeader
        showBackdrop
        closeIconProps={{}}
        onCloseIconPress={cancelAndClose}
        onBottomSheetClose={onSheetDidClose}
        sectionTitle={t('flightsSearchPage.cabinClass.guestAndCabinClassLabel')}
      >
        <View style={styles.paxClassMobContainer}>
          {passengerSelectionComponent}
          {isSnackBarVisible ? renderSnackBar() : null}
        </View>
        <StickyBottomBarView
          primaryButtonLabel={t('flightsSearchPage.cabinClass.confirm')}
          primaryButtonOnPress={donePress}
          showDivider={false}
        />
      </BottomSheet>
    );
  },
);

const styles = StyleSheet.create((pd) => ({
  // Visual overlay only (does NOT block input)
  visualOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 11,
  },

  // Fallback card style if we haven't measured yet
  portalFallbackCard: {
    position: 'absolute',
    top: 120,
    right: 16,
    width: DROPDOWN_WIDTH,
    borderRadius: 18,
    overflow: 'hidden',
    backgroundColor: getColor(pd.sys.colour.container_none),
    borderWidth: 1,
    borderColor: getColor(pd.sys.colour.outline_medium),
    zIndex: 12,
  },

  // Native styles you already had
  paxClassMobContainer: {
    height: Constants.PASSENGER_CLASS_HEIGHT,
    marginTop: 20,
  },
}));

export default PassengerClassBottomSheetView;
